import { ResourceCard } from "@/components/resources/ResourceCard";
import type { ResourceItem } from "@/data/content";
import { useAppTranslation } from "@/i18n/useAppTranslation";

type Props = {
  items: ResourceItem[];
  onAccessRequest: (resource: ResourceItem) => void;
  /** Show at most this many resources (home preview). */
  limit?: number;
};

/** Placeholder cards while resources load from the API. */
export function ResourcesGridSkeleton({ label, count = 2 }: { label: string; count?: number }) {
  return (
    <div role="status" aria-label={label} className="grid gap-6 md:grid-cols-2">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} aria-hidden="true" className="overflow-hidden rounded-[1.75rem] border border-brand-primary/8 bg-white shadow-lit">
          <div className="aspect-[3/2] animate-pulse bg-teal-soft motion-reduce:animate-none" />
          <div className="space-y-3 p-6 sm:p-7">
            <div className="h-6 w-2/3 animate-pulse rounded-full bg-brand-primary/10 motion-reduce:animate-none" />
            <div className="h-3 w-full animate-pulse rounded-full bg-brand-primary/8 motion-reduce:animate-none" />
            <div className="h-3 w-4/5 animate-pulse rounded-full bg-brand-primary/8 motion-reduce:animate-none" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ResourcesGrid({ items, onAccessRequest, limit }: Props) {
  const { t } = useAppTranslation("resourcesPage");
  const active = items.filter((res) => !res.inactive);
  const visible = limit ? active.slice(0, limit) : active;

  if (visible.length === 0) {
    return <p className="rounded-2xl bg-white p-8 text-center text-ink-muted">{t("empty")}</p>;
  }

  return (
    <ul className={`grid gap-6 md:grid-cols-2 ${visible.length >= 3 ? "lg:grid-cols-3" : ""}`}>
      {visible.map((res, i) => (
        <ResourceCard key={res.id ?? res.title} resource={res} onAccessRequest={onAccessRequest} index={i} />
      ))}
    </ul>
  );
}
